import { useDeferredValue, useEffect } from 'react';

import { useLocation, useNavigate } from 'react-router';

import { useAtomValue, useSetAtom } from 'jotai';
import { focusAtom } from 'jotai-optics';
import { useTranslation } from 'react-i18next';

import { sendNativeMessage } from '@/hooks/useNativeListener';

import { cn } from '@/utils/tailwind';
import { useRouteMatcher } from '@/utils/url';

import ConfirmationModal from '@/components/popups/ConfirmationModal';

import Constants, { routes } from '@/constants';

import { LocationState } from '@/types/types';

import { store } from '@/atoms';
import {
  trainTogetherModeAtom,
  trainTogetherModeDispatchAtom,
} from '@/atoms/trainTogetherMode';

interface TrainTogetherModeWrapperProps {
  children: React.ReactNode;
}

const isTrainTogetherModeAtom = focusAtom(trainTogetherModeAtom, o =>
  o.prop('isTrainTogetherMode'),
);

const activePlanIdAtom = focusAtom(trainTogetherModeAtom, o =>
  o.prop('activePlanId'),
);

const TrainTogetherModeWrapper: React.FC<TrainTogetherModeWrapperProps> = ({
  children,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const routeMatches = useRouteMatcher();
  const isTrainTogetherMode = useAtomValue(isTrainTogetherModeAtom);
  const sendTrainTogetherModeAction = useSetAtom(trainTogetherModeDispatchAtom);

  const locState = location.state as LocationState | undefined;

  const isTrainTogetherModeView =
    routeMatches.includes(Constants.ROUTE_PATH_GROUPS.TrainTogetherModePath) ||
    routeMatches.includes(Constants.ROUTE_PATH_GROUPS.StepDocumentPath);

  const deferredIsTrainTogetherMode = useDeferredValue(isTrainTogetherMode);

  useEffect(() => {
    if (locState?.toTrainTogetherMode) {
      sendTrainTogetherModeAction('enterTrainTogetherMode');
    }
  }, [sendTrainTogetherModeAction, locState?.toTrainTogetherMode]);

  const backToTrainTogetherMode = () => {
    navigate({
      pathname: routes.plans.trainTogether({
        planId: store.get(activePlanIdAtom),
      }),
    });
  };

  const onExitTrainTogetherMode = () => {
    sendTrainTogetherModeAction('exitTrainTogetherMode');
    sendTrainTogetherModeAction('resetTrainTogetherModeData');

    if (window.native.isNative) {
      sendNativeMessage({ type: 'trainTogetherModeActive', payload: false });
      sendNativeMessage({ type: 'navigateTo', payload: '/' });
      return;
    }

    navigate({
      pathname: '/',
    });
  };

  const shouldOpenExitTrainTogetherModeModal =
    deferredIsTrainTogetherMode && !isTrainTogetherModeView;

  return (
    <div
      className={cn(
        isTrainTogetherMode &&
          'fixed top-0 left-0 size-full overflow-auto border-8 border-[#3EB1C8]',
      )}
    >
      <span
        className={cn(
          isTrainTogetherMode && !isTrainTogetherModeView && 'blur-xl',
        )}
      >
        {children}
      </span>
      <ConfirmationModal
        bodyText={t('TrainingPlans.trainTogether.exitModalBody')}
        headerText={t('TrainingPlans.trainTogether.exitModalHeader')}
        isOpen={shouldOpenExitTrainTogetherModeModal}
        primaryButtonColor={'secondary'}
        primaryButtonHandler={onExitTrainTogetherMode}
        primaryButtonText={t('Button.exit')}
        secondaryButtonHandler={backToTrainTogetherMode}
        secondaryButtonText={t('Button.backToTrainTogetherMode')}
        showCloseButton={false}
      />
    </div>
  );
};

export default TrainTogetherModeWrapper;
